// Top-bar notifications bell for the new shell.
//
// A 36px icon button with an unread badge that opens a popover:
// newest first, each row links straight to the item and marks it
// read; "Mark all as read"; and a "Notification settings" view. Data
// comes from useNotifications() (shared, polled, cached across the
// shell's per-page remounts).
//
// Self-contained so the top bar can mount it with no props:
//   <NotificationsBell />

import { useCallback, useEffect, useId, useRef, useState, type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  CheckCheck,
  ClipboardCheck,
  Loader2,
  KeyRound,
  Radar,
  Send,
  Settings,
  ShieldAlert,
  ShieldOff,
  TrendingUp,
  Unplug,
} from 'lucide-react';
import { cn } from '@cyber-aware/ui';
import { useAuth } from '../../auth/AuthContext';
import { useNotifications, type AppNotification, type NotificationKind, type NotificationSeverity } from '../../lib/notifications';
import { bellAriaLabel, relativeTime, supportReference, unreadBadgeText } from '../../lib/notificationFormat';
import { NotificationSettings } from './NotificationSettings';

/** Floating-layer surface shared with the shell's other pop-outs. */
const FLOATING_PANEL =
  'border border-slate-200 bg-white shadow-xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900';

const KIND_ICON: Record<NotificationKind, ComponentType<{ className?: string }>> = {
  approval_requested: ClipboardCheck,
  connector_failing: Unplug,
  phishing_campaign_sent: Send,
  automated_action_taken: ShieldAlert,
  exception_update: ShieldOff,
  umbra_decoy_hit: Radar,
  umbra_spa_failed: KeyRound,
  tracked_query_rise: TrendingUp,
};

const SEVERITY_CHIP: Record<NotificationSeverity, string> = {
  info: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  critical: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
};

const TEXT_BUTTON =
  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:pointer-events-none disabled:opacity-50 dark:text-indigo-300 dark:hover:bg-indigo-950';

export function NotificationsBell() {
  const { session } = useAuth();
  const n = useNotifications();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'list' | 'settings'>('list');
  const [retrying, setRetrying] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const headingId = useId();

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setView('list');
    setActionError(null);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Close on Escape and on a click outside.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, close]);

  // Move focus into the panel when it opens or switches view.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open, view]);

  const { refresh } = n;
  const toggle = () => {
    if (open) {
      close(false);
      return;
    }
    setOpen(true);
    void refresh();
  };

  const retry = async () => {
    setRetrying(true);
    try {
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  const markAll = async () => {
    setActionError(null);
    try {
      await n.markAllRead();
    } catch (err) {
      const ref = supportReference(err instanceof Error ? err.message : String(err));
      setActionError(`Couldn’t mark everything as read. Try again.${ref ? ` (Reference ${ref})` : ''}`);
    }
  };

  const openItem = (item: AppNotification) => {
    close(false);
    // markRead refetches on failure, so the item simply shows as
    // unread again; navigation goes ahead either way.
    n.markRead(item.id).catch(() => undefined);
  };

  if (!session?.activeOrgId) return null;

  const badge = unreadBadgeText(n.unreadCount);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={bellAriaLabel(n.unreadCount)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {badge && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 min-w-[1.125rem] rounded-full bg-rose-600 px-1 text-center text-[10px] font-semibold leading-[1.125rem] text-white ring-2 ring-white dark:ring-slate-900"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-labelledby={view === 'list' ? headingId : undefined}
          aria-label={view === 'settings' ? 'Notification settings' : undefined}
          tabIndex={-1}
          style={{ opacity: 1 }}
          className={cn(
            'absolute right-0 top-full z-50 mt-2 flex max-h-[min(32rem,calc(100vh-6rem))] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg focus:outline-none',
            FLOATING_PANEL,
          )}
        >
          {view === 'settings' ? (
            <div className="overflow-y-auto p-3">
              <button type="button" onClick={() => setView('list')} className={cn(TEXT_BUTTON, 'mb-2 -ml-1')}>
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to notifications
              </button>
              <NotificationSettings />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-700">
                <h2 id={headingId} className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Notifications
                </h2>
                <button
                  type="button"
                  onClick={() => void markAll()}
                  disabled={n.unreadCount === 0}
                  className={TEXT_BUTTON}
                >
                  <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" /> Mark all as read
                </button>
              </div>

              {actionError && (
                <p role="alert" className="border-b border-slate-200 px-3 py-2 text-xs text-rose-700 dark:border-slate-700 dark:text-rose-300">
                  {actionError}
                </p>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto">
                {n.error && !n.loaded ? (
                  <div role="alert" className="px-3 py-6 text-center">
                    <p className="text-sm text-slate-700 dark:text-slate-200">We couldn’t load your notifications right now.</p>
                    {/* Never the raw error: at most the support reference. */}
                    {supportReference(n.error) && (
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        If this keeps happening, contact support with reference {supportReference(n.error)}.
                      </p>
                    )}
                    <button type="button" onClick={() => void retry()} disabled={retrying} className={cn(TEXT_BUTTON, 'mt-2')}>
                      {retrying && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Try again
                    </button>
                  </div>
                ) : n.loading ? (
                  <p className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-slate-500 dark:text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading notifications…
                  </p>
                ) : n.items.length === 0 ? (
                  <p className="px-3 py-8 text-center text-sm text-slate-600 dark:text-slate-300">You’re all caught up.</p>
                ) : (
                  <>
                    {n.error && (
                      <p role="status" className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
                        Couldn’t refresh. Showing what we had.
                        <button type="button" onClick={() => void retry()} disabled={retrying} className={TEXT_BUTTON}>
                          Try again
                        </button>
                      </p>
                    )}
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                      {n.items.map((item) => (
                        <NotificationRow key={item.id} item={item} onOpen={openItem} />
                      ))}
                    </ul>
                    {n.hasMore && (
                      <div className="border-t border-slate-100 p-2 text-center dark:border-slate-800">
                        <button type="button" onClick={() => void n.loadMore()} disabled={n.loadingMore} className={TEXT_BUTTON}>
                          {n.loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Show older
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="border-t border-slate-200 px-3 py-2 dark:border-slate-700">
                <button type="button" onClick={() => setView('settings')} className={cn(TEXT_BUTTON, '-ml-1')}>
                  <Settings className="h-3.5 w-3.5" aria-hidden="true" /> Notification settings
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationRow({ item, onOpen }: { item: AppNotification; onOpen: (item: AppNotification) => void }) {
  const Icon = KIND_ICON[item.kind] ?? Bell;
  const unread = item.readAt === null;
  return (
    <li>
      <Link
        to={item.href}
        onClick={() => onOpen(item)}
        className="flex gap-3 px-3 py-2.5 hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:hover:bg-slate-800 dark:focus-visible:bg-slate-800"
      >
        <span
          className={cn('mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md', SEVERITY_CHIP[item.severity])}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-sm text-slate-900 dark:text-slate-100',
              unread ? 'font-semibold' : 'font-medium',
            )}
          >
            {item.title}
          </span>
          <span className="block truncate text-xs text-slate-600 dark:text-slate-300">{item.body}</span>
          <span className="mt-0.5 block text-[11px] text-slate-500 dark:text-slate-400">
            <time dateTime={new Date(item.createdAt).toISOString()}>{relativeTime(item.createdAt)}</time>
          </span>
        </span>
        {unread && (
          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-indigo-600 dark:bg-indigo-400">
            <span className="sr-only">Unread</span>
          </span>
        )}
      </Link>
    </li>
  );
}
